import type {
  QueryFunctionContext,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groq from 'groq';
import {
  searchBenefits,
  searchBlogArticles,
  searchPodcasts,
  searchProducts,
  searchScience,
  searchSkincareIngredients,
  searchSkincareScience,
  searchStudies,
} from 'lib/queries/search';
import { client } from 'lib/sanity';
import type { SiteCategory } from 'types/common/SiteCategory';

import { BENEFITS_PAGE_REFS } from '../pages/useBenefitsPage';
import { useIsSkincare } from '../useIsSkincare';
import { usePlpMenuProducts } from '../usePlpMenuProducts';
import type {
  NormalizedResult,
  Result,
  ResultCategory,
  SearchResults,
  SiteSearchResult,
} from './types';
import { normalizeWeights } from './utils';

export const SITE_SEARCH_KEY = 'siteSearch';

export type UseSiteSearchQueryKey = [string, string[], SiteCategory, string?];

const MAX_RESULTS = 15;

/* NOTE: Each search category has a different number of scoring rules in its
 * GROQ score() block, so raw _score values are not comparable across categories.
 * To normalize, we record the max possible score for each query. This is
 * calculated by summing each rule's contribution: a boost(condition, N)
 * contributes N, and an unboosted rule contributes 1. eg:
 *
 *    products: 9 == boost(title,2) + boost(ingredients,2) + 5 unboosted
 *    podcasts: 7 == boost(title,3) + boost(summary,2) + body + transcript
 *
 * Dividing each item's _score by its category's max possible score gives a
 * 0-1 value representing how strongly it matched relative to what was possible.
 * */
const MAX_POSSIBLE_SCORES: Record<ResultCategory, number> = {
  product: 10,
  timeline_article: 6,
  timeline_podcast: 7,
  timeline_scienceV2: 12,
  timeline_studies: 5,
  timeline_skincareIngredients: 1,
  timeline_benefitsPage: 9,
  timeline_skincareBenefitsPage: 9,
  timeline_skincareScience: 9,
};

/* INFO: The weights below allow us to manually weight each category.
 * Generally we want products to surface more readily so they are weighted
 * slightly higher. When searching "softgels" for example we want the
 * product to be the first result even though we have many articles which
 * would contain the word many times.
 *
 * These values are normalized to sum to 1, so they express relative
 * importance between categories rather than being tied to any specific
 * score scale. */
const WEIGHTS: Record<ResultCategory, number> = normalizeWeights({
  product: 4,
  timeline_article: 1,
  timeline_podcast: 1,
  timeline_scienceV2: 3,
  timeline_studies: 1,
  timeline_skincareIngredients: 1,
  timeline_benefitsPage: 3,
  timeline_skincareBenefitsPage: 3,
  timeline_skincareScience: 3,
});

function normalizeAndMerge(results: SearchResults) {
  const searchItems = Object.values(results) as Result[][];

  const merged = searchItems.reduce<NormalizedResult[]>((acc, items) => {
    if (!Array.isArray(items) || items.length === 0) return acc;

    for (const item of items) {
      acc.push({
        ...item,
        normalizedScore:
          (item._score / MAX_POSSIBLE_SCORES[item._type]) * WEIGHTS[item._type],
      });
    }

    return acc;
  }, []);

  return merged
    .sort((a, b) => b.normalizedScore - a.normalizedScore)
    .slice(0, MAX_RESULTS);
}

export const fetchSearchResults = async ({
  queryKey,
}: QueryFunctionContext<UseSiteSearchQueryKey>): Promise<SiteSearchResult> => {
  const [, productSlugs, siteCategory, searchTerm] = queryKey;

  if (!searchTerm) {
    return { results: [], totalResults: 0 };
  }

  const isSkincare = siteCategory === 'skincare';

  const query = groq`
    {
      "products": ${searchProducts({ productSlugs, siteCategory })},
      "blogArticles": ${searchBlogArticles()},
      "podcasts": ${searchPodcasts()},
      "science": ${isSkincare ? searchSkincareScience() : searchScience()},
      "studies": ${searchStudies()},
      "skincareIngredients": ${isSkincare ? searchSkincareIngredients() : '[]'},
      "benefits": ${searchBenefits}
    }
  `;

  const rawResults = await client.fetch<SearchResults>(query, {
    searchTerm: `*${searchTerm}*`,
    benefitsPageType: BENEFITS_PAGE_REFS[siteCategory].documentType,
  });

  const results = normalizeAndMerge(rawResults);

  return { results, totalResults: results.length };
};

export const useSiteSearch = (
  searchTerm: string,
): UseQueryResult<SiteSearchResult, unknown> => {
  const isSkincare = useIsSkincare();
  const { data: productSlugs } = usePlpMenuProducts();

  const siteCategory = isSkincare ? 'skincare' : 'supplements';

  return useQuery({
    queryKey: [SITE_SEARCH_KEY, productSlugs || [], siteCategory, searchTerm],
    queryFn: fetchSearchResults,
    enabled: searchTerm !== '',
  });
};
